/**
 * AnalysisResultView — json-render powered analysis result view (PoC)
 *
 * Renders DramaDetector analysis results via the json-render catalog.
 * This is the PoC component for NocoDB #783 — demonstrates how LLM-generated
 * specs are rendered from the DD_COMPONENT_CATALOG.
 *
 * Rendering pipeline:
 *   AnalysisResultView
 *     └── DDRenderer (Renderer from @json-render/react-native) + ddRegistry
 *           └── Components from DD_COMPONENT_CATALOG
 *
 * Fallback: If spec rendering fails or spec is null, renders a static layout
 * using the same catalog components directly — no white screen, ever.
 *
 * Feature flag: Controlled by USE_JSON_RENDER_POC env/feature flag.
 * Does NOT replace any existing layout — opt-in PoC only.
 *
 * Component mapping (Sally conceptual → registered catalog name):
 *   ActionItem  → ActionButton (catalog registered name)
 *   CrisisAlert → NarrativeBlock with emphasis="critical" (catalog registered name)
 *
 * Architecture ref: Winston (Discourse #3543/2)
 * Catalog spec ref: Sally (Discourse #3543/4)
 * Task: P1.4 [NocoDB #783]
 * Agent: Diego <diego@liftping.net>
 */

"use client";

import React, { useState } from "react";
import { ddRegistry, DDRenderer } from "./catalog";
import { JSONUIProvider } from "@json-render/react-native";
import { DD_CORE_COMPONENT_NAMES } from "./component-catalog";

// NOTE: $computed functions (formatScore, formatCurrency, etc.) are not supported
// by @json-render/react-native's JSONUIProvider. They were previously passed via
// the `functions` prop on @json-render/react's provider. If $computed support is
// needed, either:
// 1. Resolve computed expressions server-side before sending the spec
// 2. Wait for @json-render/react-native to add functions support
// 3. Use $state expressions with pre-computed values in the state model
// The production path (DDGenerativeResults) never used $computed — only this PoC.
import type { Spec } from "@json-render/core";

// ============================================================================
// Types
// ============================================================================

/** Subset of DramaAnalysisResponse used by this PoC component */
export interface AnalysisResultInput {
  /** Analysis ID */
  id: string;
  /** Drama score (0-10) */
  score: number;
  /** Drama category */
  category: "safe" | "caution" | "high_drama" | "extreme_drama";
  /** Narrative summary from the LLM */
  summary: string;
  /** Action items / suggestion texts */
  actions?: string[];
  /** User subscription tier */
  userTier?: "free" | "drama_plus" | "dramax";
}

export interface AnalysisResultViewProps {
  /** The analysis output to render */
  analysis: AnalysisResultInput;
  /**
   * Optional pre-generated json-render spec.
   * When provided, renders it directly.
   * When null/undefined, builds a PoC spec from the analysis props.
   */
  spec?: Spec | null;
  /** Called when an action is triggered via the catalog */
  onAction?: (actionId: string, params?: Record<string, unknown>) => void;
  /** Test ID */
  testID?: string;
}

// ============================================================================
// PoC spec builder
// ============================================================================

/**
 * buildPoCSpec — Builds a minimal json-render spec from analysis props.
 *
 * This replaces the LLM call for the PoC — the real implementation
 * (generate-ui-spec edge function) generates this spec from the LLM.
 * The PoC demonstrates that the same components render correctly from
 * a spec, not from static JSX.
 *
 * Demonstrates all 6 of Sally's core catalog concepts:
 *   AnalysisCard  → AnalysisCard (registered)
 *   DramaScore    → DramaScore (registered)
 *   TierBadge     → TierBadge (registered)
 *   NarrativeBlock→ NarrativeBlock (registered)
 *   ActionItem    → ActionButton (registered name in ddCatalog)
 *   CrisisAlert   → NarrativeBlock[emphasis=critical] (inline crisis banner
 *                   using existing registered component; full CrisisAlert
 *                   component is spec'd in DD_COMPONENT_CATALOG for future
 *                   registry extension)
 */
export function buildPoCSpec(analysis: AnalysisResultInput): Spec {
  const { score, category, summary, actions = [], userTier = "free" } = analysis;

  const isCrisis = category === "extreme_drama";
  const isHighDrama = category === "high_drama" || category === "extreme_drama";

  const elements: Spec["elements"] = {};
  const rootChildren: string[] = [];

  // --- CrisisAlert (Sally catalog concept #6) ---
  // Rendered via NarrativeBlock[emphasis=critical] using the registered catalog.
  // The DD_COMPONENT_CATALOG.CrisisAlert entry defines the full spec for future
  // implementation. The PoC maps it to NarrativeBlock to avoid gaps in the registry.
  if (isCrisis) {
    elements["crisis-alert"] = {
      type: "NarrativeBlock",
      props: {
        text: "⚠️ Extreme drama detected. Consider pausing before responding.",
        emphasis: "critical",
      },
      children: [],
    };
    rootChildren.push("crisis-alert");
  }

  // --- DramaScore (Sally catalog concept #2) ---
  // Hero element — always first real content.
  elements["drama-score"] = {
    type: "DramaScore",
    props: {
      score,
      category,
      size: isHighDrama ? "lg" : "md",
      showLabel: true,
    },
    children: [],
  };
  rootChildren.push("drama-score");

  // --- TierBadge (Sally catalog concept #3) ---
  elements["tier-badge"] = {
    type: "TierBadge",
    props: {
      tier: userTier,
      showUpgradeHint: userTier === "free",
    },
    children: [],
  };
  rootChildren.push("tier-badge");

  // --- NarrativeBlock (Sally catalog concept #4) ---
  if (summary) {
    elements["narrative-summary"] = {
      type: "NarrativeBlock",
      props: {
        text: summary,
        emphasis: isCrisis ? "critical" : "normal",
      },
      children: [],
    };
    rootChildren.push("narrative-summary");
  }

  // --- ActionItem(s) (Sally catalog concept #5) ---
  // Rendered via ActionButton (registered name). DD_COMPONENT_CATALOG uses
  // "ActionItem" as the conceptual name (Sally's spec); the ddCatalog registers
  // it as "ActionButton". The buildPoCSpec maps them correctly.
  const limitedActions = actions.slice(0, 3);
  limitedActions.forEach((actionText, i) => {
    const key = `action-item-${i}`;
    elements[key] = {
      type: "ActionButton",
      props: {
        label: actionText,
        action: "dismiss_insight",
        tier: userTier,
        variant: i === 0 ? "primary" : "secondary",
        disabled: false,
      },
      children: [],
    };
    rootChildren.push(key);
  });

  // --- AnalysisCard (Sally catalog concept #1) ---
  // Root container wrapping all elements.
  elements["root-card"] = {
    type: "AnalysisCard",
    props: {
      title: "Drama Analysis",
      subtitle: `Score: ${score}/10 — ${category.replace(/_/g, " ")}`,
      variant: userTier === "dramax" ? "premium" : "standard",
      tierHint: userTier,
    },
    children: rootChildren,
  };

  return {
    root: "root-card",
    elements,
    state: {
      tier: userTier,
      score,
      category,
    },
  };
}

// ============================================================================
// Static fallback (no json-render)
// ============================================================================

/**
 * StaticFallback — Renders a minimal static layout when json-render fails.
 * Intentionally simple — just score + summary text. No external deps.
 */
function StaticFallback({ analysis }: { analysis: AnalysisResultInput }) {
  return (
    <div
      data-testid="analysis-result-static-fallback"
      style={{
        padding: "16px",
        borderRadius: "12px",
        border: "1px solid rgba(139, 92, 246, 0.3)",
        background: "rgba(139, 92, 246, 0.05)",
      }}
    >
      <div style={{ fontSize: "14px", color: "#6b7280", marginBottom: "8px" }}>
        Analysis Results (static fallback)
      </div>
      <div
        style={{ fontSize: "32px", fontWeight: 700, color: "#8b5cf6" }}
        data-testid="fallback-score"
      >
        {analysis.score}/10
      </div>
      <div
        style={{
          fontSize: "12px",
          textTransform: "uppercase",
          color: "#6b7280",
          marginBottom: "12px",
        }}
      >
        {analysis.category.replace(/_/g, " ")}
      </div>
      {analysis.summary && (
        <p style={{ fontSize: "14px", color: "#374151", lineHeight: 1.5 }}>
          {analysis.summary}
        </p>
      )}
    </div>
  );
}

// ============================================================================
// AnalysisResultView
// ============================================================================

/**
 * AnalysisResultView — PoC component for json-render integration.
 *
 * Two rendering modes:
 *   1. spec provided → render via DDRenderer (json-render)
 *   2. spec not provided → build PoC spec from analysis props, then render
 *
 * Falls back to StaticFallback if rendering throws.
 *
 * Feature-flagged via USE_JSON_RENDER_POC. When flag is false, the existing
 * static layout is used instead. This component is additive — it does NOT
 * replace any existing components.
 */
export function AnalysisResultView({
  analysis,
  spec: externalSpec,
  onAction: _onAction,
  testID = "analysis-result-view",
}: AnalysisResultViewProps) {
  const [renderError, setRenderError] = useState<Error | null>(null);

  // Use provided spec or build a PoC spec from analysis props
  const specToRender: Spec = externalSpec ?? buildPoCSpec(analysis);

  if (renderError) {
    return <StaticFallback analysis={analysis} />;
  }

  try {
    return (
      <div data-testid={testID}>
        {/* Hidden meta: surfaces catalog component list for PoC validation */}
        <div
          data-testid="json-render-poc-meta"
          data-catalog-components={DD_CORE_COMPONENT_NAMES.join(",")}
          style={{ display: "none" }}
          aria-hidden="true"
        />
        <JSONUIProvider
          registry={ddRegistry}
        >
          <DDRenderer
            spec={specToRender}
            registry={ddRegistry}
          />
        </JSONUIProvider>
      </div>
    );
  } catch (err) {
    // Catch synchronous render errors (async errors need an ErrorBoundary)
    const e = err instanceof Error ? err : new Error(String(err));
    setRenderError(e);
    return <StaticFallback analysis={analysis} />;
  }
}

// ============================================================================
// Feature flag helper
// ============================================================================

/**
 * USE_JSON_RENDER_POC — feature flag for AnalysisResultView.
 *
 * Set EXPO_PUBLIC_USE_JSON_RENDER_POC=true or NEXT_PUBLIC_USE_JSON_RENDER_POC=true
 * in your environment to enable the json-render PoC rendering path.
 *
 * When false (default), existing static layouts are used unchanged.
 * This component is purely additive — the static layout is never removed.
 */
export const USE_JSON_RENDER_POC: boolean =
  typeof process !== "undefined" &&
  (process.env["EXPO_PUBLIC_USE_JSON_RENDER_POC"] === "true" ||
    process.env["NEXT_PUBLIC_USE_JSON_RENDER_POC"] === "true");
